import React, { useContext, useState } from 'react'
import {
  DropDownMenu,
  DropDownMenuContext,
} from '@app/ui/dropdown-menu/DropDownMenu'
import {
  AdminTitle,
  BorderlessDropDownMenuItem,
  DropDownMenuItem,
} from '@app/ui/dropdown-menu/DropDownMenu.styles'
import { FormExGenderBtn } from '@app/evaluation-settings-menu/HeaderArea.styles'
import { Eval, LockStatus, TeamRole } from '@app/evaluation-core/types'
import theme from '@app/ui/theme'
import useEvaluationUser from '@common/hooks/useEvaluationUser'
import { resetHole } from '@app/evaluation-core'
import { EvalStatus } from '@app/courses/domain/ratingStatus'
import {
  EvaluationDoc,
  EvaluationDocWithForms,
} from '../../pages/CompositeCoursePage/CompositeCoursePage'
import { ResetHoleConfirmationModal } from './ResetHoleConfirmationModal'
import { useSnackbar } from '@app/snackbar'
import {
  FaTrash,
  FaTable,
  FaSync,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationCircle,
  FaCog,
} from 'react-icons/fa'

type ActionMenuItemProps = {
  label: string
  icon: React.ReactNode
  onPress?: () => void
  disabled?: boolean
}

// Every action here either opens a modal or changes the row behind the menu, so the
// menu closes first instead of hanging over what it just acted on.
const ActionMenuItem = ({
  label,
  icon,
  onPress,
  disabled,
}: ActionMenuItemProps) => {
  const { close } = useContext(DropDownMenuContext)

  return (
    <DropDownMenuItem
      disabled={disabled}
      onPress={() => {
        close()
        onPress?.()
      }}
    >
      <FormExGenderBtn>
        <span>{label}</span>
        <div>{icon}</div>
      </FormExGenderBtn>
    </DropDownMenuItem>
  )
}

interface EvaluationsListActionMenuProps {
  evaluation: any
  latestForms: any
  isActiveFinalized: boolean
  isDemoEvaluation: boolean
  onEvaluationInfo: () => void
  onRegenerateForms: () => void
  onDownloadForms: () => void
  onDeleteEvaluation: () => void
  onResetUnlockedHoles: () => void
  onResetRatingRevision: () => void
  onToggleShowForms: (evaluationId: string) => void
  onMarkAsActive?: (evaluationId: string) => void
  onMarkAsDemo?: (evaluationId: string, isCurrentlyDemo: boolean) => void
  onManageForms?: () => void
}

export const EvaluationsListActionMenu = ({
  evaluation,
  latestForms,
  isActiveFinalized,
  isDemoEvaluation,
  onEvaluationInfo,
  onRegenerateForms,
  onDownloadForms,
  onDeleteEvaluation,
  onResetUnlockedHoles,
  onResetRatingRevision,
  onToggleShowForms,
  onMarkAsActive,
  onMarkAsDemo,
  onManageForms,
}: EvaluationsListActionMenuProps) => {
  const [openSnackbar] = useSnackbar()
  const evalUser = useEvaluationUser()

  const [showResetHoleCancelModal, setShowResetHoleCancelModal] =
    useState(false)

  const closeResetHoleModal = () => setShowResetHoleCancelModal(false)
  const openResetHoleModal = () => setShowResetHoleCancelModal(true)

  const resetHoleFn = async () => {
    const realTimeEval = evaluation as Eval

    realTimeEval?.holes.forEach(async (hole) => {
      if (!realTimeEval?.id || !hole?.holeIndex) {
        console.log(`resetHoleGn - missing realTimeEval?.id or hole.holeIndex`)
        return
      }

      if (hole?.locked == LockStatus.UNLOCKED)
        await resetHole(+hole?.holeIndex + 1, realTimeEval?.id)
    })
    closeResetHoleModal()
  }

  function isEvaluationWithForms(
    evaluation: EvaluationDoc
  ): evaluation is EvaluationDocWithForms {
    const formsEval = evaluation as EvaluationDocWithForms
    return formsEval.finalizedForms !== undefined
  }

  function isEvaluationWithActualForms(
    evaluation: EvaluationDoc
  ): evaluation is EvaluationDocWithForms {
    const formsEval = evaluation as EvaluationDocWithForms
    return (
      formsEval.finalizedForms !== undefined &&
      formsEval.finalizedForms?.length > 1 - 1
    )
  }

  function isEvaluationWithMultipleForms(
    evaluation: EvaluationDoc
  ): evaluation is EvaluationDocWithForms {
    const formsEval = evaluation as EvaluationDocWithForms
    return (
      formsEval.finalizedForms !== undefined &&
      formsEval.finalizedForms?.length > 1
    )
  }

  const isAuthenticatedSystemAdmin = evalUser?.isAdmin

  return (
    <>
      <ResetHoleConfirmationModal
        closeModal={closeResetHoleModal}
        showModal={showResetHoleCancelModal}
        acceptFunction={resetHoleFn}
      />
      <DropDownMenu
        value={''}
        isMenu={true}
        width={250}
        relative
        xAdj={-150}
        useShadow={false}
      >
        {
          <BorderlessDropDownMenuItem
            onPress={async () => {
              openSnackbar(
                'This function has been temporarily disabled because of inconsistency with data until /Stoffe'
              )
            }}
          >
            {/*<RatingListInfoDropdown evaluation={evaluation} />*/}
            <FormExGenderBtn>
              <span>Detailed info</span>
              <div>
                <FaInfoCircle size={20} color={'#4190e0'} />
              </div>
            </FormExGenderBtn>
          </BorderlessDropDownMenuItem>
        }

        <>
          <AdminTitle>Team leader functions</AdminTitle>

          <ActionMenuItem
            disabled={
              !isAuthenticatedSystemAdmin &&
              !evalUser?.isTeamLeader &&
              !isActiveFinalized
            }
            onPress={() => {
              onMarkAsDemo?.(evaluation.id, isDemoEvaluation)
            }}
            label={isDemoEvaluation ? 'Currently DEMO' : 'Mark as DEMO'}
            icon={<FaExclamationCircle size={20} color={'orange'} />}
          />

          {evaluation?.status === EvalStatus.FINALIZED && (
            <ActionMenuItem
              disabled={
                !isAuthenticatedSystemAdmin &&
                !evalUser?.isTeamLeader &&
                !isActiveFinalized
              }
              onPress={() => onMarkAsActive?.(evaluation.id)}
              label={isActiveFinalized ? 'Currently Active' : 'Mark as Active'}
              icon={<FaCheckCircle size={20} color={'green'} />}
            />
          )}

          {evaluation?.status === EvalStatus.FINALIZED &&
            isEvaluationWithMultipleForms(evaluation) && (
              <ActionMenuItem
                disabled={
                  !isAuthenticatedSystemAdmin && !evalUser?.isTeamLeader
                }
                onPress={() => onToggleShowForms(evaluation.id)}
                label="Toggle show generated forms"
                icon={<FaTable size={20} color={theme.colors.primary} />}
              />
            )}

          {evaluation?.status === EvalStatus.FINALIZED && (
            <ActionMenuItem
              disabled={!isAuthenticatedSystemAdmin && !evalUser?.isTeamLeader}
              onPress={onManageForms}
              label="Manage Forms"
              icon={<FaCog size={20} color={theme.colors.primary} />}
            />
          )}

          {evaluation?.status === EvalStatus.FINALIZED && (
            <ActionMenuItem
              disabled={!isAuthenticatedSystemAdmin && !evalUser?.isTeamLeader}
              onPress={onRegenerateForms}
              label={`${
                isEvaluationWithActualForms(evaluation)
                  ? 'Regenerate'
                  : 'Generate'
              } forms`}
              icon={<FaSync size={20} color={theme.colors.primary} />}
            />
          )}

          <ActionMenuItem
            disabled={!isAuthenticatedSystemAdmin && !evalUser?.isTeamLeader}
            onPress={onDeleteEvaluation}
            label="Delete"
            icon={<FaTrash size={20} color={'rgb(238, 0, 0)'} />}
          />
        </>

        {evaluation?.status !== EvalStatus.FINALIZED && evalUser?.isMiAdmin && (
          <>
            <AdminTitle>Admin functions</AdminTitle>

            <ActionMenuItem
              disabled={!evalUser?.isMiAdmin}
              onPress={openResetHoleModal}
              label="Reset unlocked holes"
              icon={<FaSync size={20} color={theme.colors.primary} />}
            />
          </>
        )}
        {evaluation?.status !== EvalStatus.FINALIZED &&
          evalUser?.isMiAdmin &&
          evaluation?.ratingRevision && (
            <>
              <ActionMenuItem
                disabled={!evalUser?.isMiAdmin}
                onPress={onResetRatingRevision}
                label="Reset rating revision"
                icon={<FaSync size={20} color={theme.colors.primary} />}
              />
            </>
          )}
      </DropDownMenu>
    </>
  )
}
